import { LoadingSpinner } from '../shared/components';
import { emptyState } from '../shared/components/Narrative';
import { Formatters } from '../shared/lib/formatters';
import { QuickLogIssue } from '../shared/components/QuickLogIssue';
import { askAfterPhoto } from '../shared/components/AfterPhotoPrompt';
import { woChipLabel } from './issues/woSync';
import { enqueuePhotos, pendingItems } from '../lib/photoUploader';   // R116 #5: every photo / clip rides the persisted background queue
import { mediaStrip, bindMediaStrips, toMediaItems, pendingTilesHtml, patchPendingProgress, mergeLandedMedia, EVIDENCE_ACCEPT, pickSummary } from '../shared/lib/media';
import { isBoardUnknown, offerBoardRetry, BOARD_UNKNOWN_MSG } from '../shared/lib/boardRetry';   // review R120 #14 (round 6)

// Quick Log — the dedicated home for the walk-a-property-and-log-everything flow
// (R23). Big capture button + a visual gallery of everything logged, filterable
// by property, with one-click routing. The same issues also appear on the master
// Issues board; this page is the capture-first, photo-forward showcase.
const API = '/api/assetops';

const SEV_STYLE: Record<string, string> = {
  emergency: 'background:#fef2f2; color:#b91c1c; border:1px solid #fecaca;',
  high: 'background:#fff7ed; color:#c2410c;',
  med: 'background:#fefce8; color:#a16207;',
  low: 'background:#f1f5f9; color:#64748b;',
};
// R116 #6: a quick-log routed to a work order shows THAT work order (the board
// now carries it: wo_id + wo {chip, done, total}) — 90 routed logs used to look
// unassigned. Tones match the Issues board's chips (tokens only).
const WO_TONE: Record<string, string> = {
  done: 'background:var(--success-soft); color:var(--success-text);', item_done: 'background:var(--success-soft); color:var(--success-text);',
  vendor_done: 'background:var(--accent-soft); color:var(--accent);', missing: 'background:var(--bg-subtle); color:var(--text-muted);', unknown: 'background:var(--bg-subtle); color:var(--text-muted);',
};
const woTone = (chip: string) => WO_TONE[chip] || 'background:var(--warning-soft); color:var(--warning-text);';
const CAT_EMOJI: Record<string, string> = { maintenance: '🔧', cleaning: '🧹', ffe: '🛋', damage: '💥', supply: '📦', safety: '⚠️', access: '🔑', other: '•' };
const ROUTE_TARGETS = [
  { v: 'work_order', label: '🔧 Work order' },
  { v: 'next_clean', label: '🧹 Next clean' },
  { v: 'inspection', label: '🔎 Next inspection' },
  { v: 'supply', label: '📦 Supply request' },
  { v: 'strike', label: '🚩 Strike review' },
];

export class QuickLogPage {
  private container: HTMLElement;
  private issues: any[] = [];
  private roster: { name: string; kind: string }[] = [];
  private fListing = 'all';
  private fStatus = 'open';
  private fFrom = '';   // R39: date-range filter over the LOGGED day (device-local)
  private fTo = '';
  // R42: with ~500 issues (and growing) a full render janks — cap the cards
  // painted per pass; "Show more" extends. Resets when any filter changes.
  private shown = 80;
  private groupByProp = false;
  private loading = false;
  private detail: any = null;   // open detail modal payload
  private selectMode = false;             // R35 #5: multi-select for bulk delete
  private selected = new Set<string>();   // selected issue source_ids
  private bulkBusy = false;

  constructor(container: HTMLElement) { this.container = container; this.listenEvidence(); }
  private esc(s: any): string { return Formatters.escapeHtml(s == null ? '' : String(s)); }

  async render(): Promise<void> {
    this.container.innerHTML = `<div class="page-content"><div id="ql-body"><div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div></div></div>`;
    this.wireLifecycleRefresh();
    await this.load();
    this.draw();
  }

  // R27b (Mark: card showed no photo, "but if you click on it it's there") —
  // the card list goes stale two ways: background photo uploads finish AFTER
  // the list was fetched, and iOS Safari's back-navigation restores the page
  // from bfcache without re-running anything. Quietly re-pull the list when
  // the page comes back into view. Guards: only when this page's DOM is live,
  // and never while a detail modal is open (a redraw would disrupt it).
  private lifecycleWired = false;
  private wireLifecycleRefresh(): void {
    if (this.lifecycleWired) return;
    this.lifecycleWired = true;
    window.addEventListener('pageshow', (e: PageTransitionEvent) => { if (e.persisted) void this.quietRefresh(); });
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') void this.quietRefresh(); });
  }
  private async quietRefresh(): Promise<void> {
    if (!this.container.querySelector('#ql-body') || this.detail) return;
    await this.load();
    if (this.container.querySelector('#ql-body') && !this.detail) this.draw();
  }

  private loadSeq = 0;   // R35: a stale quietRefresh must never overwrite a fresher load (e.g. resurrect just-deleted cards)
  private async load(): Promise<void> {
    const seq = ++this.loadSeq;
    const [board, v, c, m, team] = await Promise.all([
      fetch(`${API}/issues/board?source=logged&days=365&include_older=1`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/vendors`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/cleaners`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/inspections/managers`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`/api/tasks/assignees`).then(r => r.ok ? r.json() : null).catch(() => null),
    ]);
    if (seq !== this.loadSeq) return;   // superseded by a newer load
    this.issues = ((board && board.issues) || []).filter((i: any) => i.source === 'logged');
    const ROLE: Record<string, string> = { admin: 'Admin', director: 'Director', manager: 'Manager', remote_ops: 'Remote ops', maintenance: 'Maintenance', inspector: 'Inspector', cleaner: 'Cleaner', owner: 'Owner' };
    const out: { name: string; kind: string }[] = []; const seen = new Set<string>();
    const add = (n: string, k: string) => { const key = n.trim().toLowerCase(); if (n.trim() && !seen.has(key)) { seen.add(key); out.push({ name: n.trim(), kind: k }); } };
    (((c && c.cleaners) || []).filter((x: any) => x.active !== false)).forEach((x: any) => add(String(x.name || ''), 'Cleaner'));
    ((((m && (m.managers || m.inspectors)) || []))).forEach((x: any) => add(String(x.name || ''), 'Inspector'));
    (((team && team.people) || [])).forEach((x: any) => add(String(x.full_name || x.email || ''), ROLE[x.role] || 'Team'));
    (((v && v.vendors) || [])).forEach((x: any) => add(String(x.name || ''), 'Vendor'));
    this.roster = out;
  }

  // R39: created_at is a UTC ISO timestamp — bucket to the DEVICE-local
  // calendar day (an issue logged after ~5pm PT carries tomorrow's UTC date;
  // slicing the ISO string would file it on the wrong day).
  private localDay(iso: string): string {
    try { return new Date(iso).toLocaleDateString('en-CA'); } catch { return String(iso || '').slice(0, 10); }
  }
  private filtered(): any[] {
    return this.issues.filter(i =>
      (this.fListing === 'all' || String(i.listing_id) === this.fListing) &&
      (this.fStatus === 'all' || i.status === this.fStatus) &&
      (!this.fFrom || this.localDay(i.created_at) >= this.fFrom) &&
      (!this.fTo || this.localDay(i.created_at) <= this.fTo));
  }

  private draw(): void {
    const body = document.getElementById('ql-body');
    if (!body) return;
    const all = this.issues;
    const weekAgo = Date.now() - 7 * 86400000;
    const kpis = {
      total: all.length,
      open: all.filter(i => i.status === 'open').length,
      week: all.filter(i => (Date.parse(i.created_at) || 0) >= weekAgo).length,
      photos: all.filter(i => i.photos).length,
    };
    const props = [...new Map(all.map(i => [String(i.listing_id), i.listing_name])).entries()]
      .map(([id, name]) => ({ id, name })).sort((a, b) => String(a.name).localeCompare(String(b.name)));
    const rows = this.filtered();

    body.innerHTML = `
      <style>
        .ql-hero { display:flex; align-items:center; justify-content:space-between; gap:16px; flex-wrap:wrap; background:linear-gradient(135deg, var(--accent,#2563eb), #60a5fa); color:#fff; border-radius:16px; padding:20px 22px; margin-bottom:16px; }
        .ql-hero h2 { margin:0; font-size:21px; font-weight:700; }
        .ql-hero p { margin:4px 0 0; font-size:13px; opacity:.9; max-width:520px; }
        .ql-log-btn { background:#fff; color:var(--accent,#2563eb); border:none; border-radius:12px; padding:14px 22px; font-size:16px; font-weight:700; cursor:pointer; box-shadow:0 6px 20px rgba(0,0,0,.18); white-space:nowrap; }
        .ql-kpis { display:grid; grid-template-columns:repeat(auto-fit,minmax(120px,1fr)); gap:10px; margin-bottom:16px; }
        .ql-kpi { background:var(--card,#fff); border:1px solid var(--border,#e2e8f0); border-radius:12px; padding:12px 14px; }
        .ql-kpi-v { font-size:22px; font-weight:700; color:var(--text,#0f172a); font-variant-numeric:tabular-nums; }
        .ql-kpi-l { font-size:11.5px; color:var(--text-muted,#64748b); text-transform:uppercase; letter-spacing:.04em; }
        .ql-filters { display:flex; gap:8px; flex-wrap:wrap; align-items:center; margin-bottom:14px; }
        .ql-sel { padding:8px 11px; border:1px solid var(--border,#e2e8f0); border-radius:9px; font-size:13px; background:var(--card,#fff); }
        .ql-chip { font-size:12.5px; font-weight:600; padding:7px 13px; border:1px solid var(--border,#e2e8f0); border-radius:999px; background:var(--card,#fff); color:var(--text-muted,#64748b); cursor:pointer; }
        .ql-chip.on { background:var(--text,#0f172a); color:#fff; border-color:var(--text,#0f172a); }
        .ql-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(190px,1fr)); gap:12px; }
        .ql-card { position:relative; background:var(--card,#fff); border:1px solid var(--border,#e2e8f0); border-radius:14px; overflow:hidden; cursor:pointer; transition:transform .12s, box-shadow .12s; content-visibility:auto; contain-intrinsic-size: 230px; }
        .ql-card:hover { transform:translateY(-2px); box-shadow:0 10px 26px rgba(0,0,0,.1); }
        .ql-ph { position:relative; width:100%; aspect-ratio:4/3; background:var(--bg-subtle,#f1f5f9); display:flex; align-items:center; justify-content:center; font-size:40px; }
        .ql-ph img, .ql-ph video { width:100%; height:100%; object-fit:cover; }
        .ql-ph .ql-nph { font-size:42px; opacity:.5; }
        .ql-sev-tag { position:absolute; top:8px; left:8px; font-size:10px; font-weight:700; padding:2px 8px; border-radius:999px; text-transform:uppercase; }
        .ql-pn { position:absolute; top:8px; right:8px; background:rgba(15,23,42,.7); color:#fff; font-size:11px; padding:2px 7px; border-radius:999px; }
        .ql-cbody { padding:10px 12px; }
        .ql-ctitle { font-size:13.5px; font-weight:600; color:var(--text,#0f172a); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .ql-cmeta { font-size:11.5px; color:var(--text-muted,#94a3b8); margin-top:3px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .ql-cstatus { display:inline-block; margin-top:6px; font-size:10.5px; font-weight:600; padding:1px 8px; border-radius:999px; }
        .st-open { background:#eff6ff; color:#1d4ed8; } .st-in_progress { background:#fefce8; color:#a16207; } .st-resolved { background:#f0fdf4; color:#15803d; } .st-dismissed { background:#f1f5f9; color:#94a3b8; }
        .ql-modal-wrap { position:fixed; inset:0; z-index:9000; display:flex; align-items:center; justify-content:center; }
        .ql-modal-bg { position:absolute; inset:0; background:rgba(15,23,42,.55); }
        .ql-modal { position:relative; width:100%; max-width:560px; max-height:92vh; overflow:auto; background:var(--card,#fff); border-radius:16px; box-shadow:0 20px 60px rgba(0,0,0,.3); padding:18px; }
        .ql-modal h3 { margin:0 0 2px; font-size:17px; }
        .ql-modal-sub { font-size:12.5px; color:var(--text-muted,#64748b); margin-bottom:12px; }
        .ql-media { display:flex; flex-wrap:wrap; gap:6px; margin:10px 0; }
        .ql-media img, .ql-media video { max-width:160px; max-height:160px; border-radius:8px; border:1px solid var(--border,#e2e8f0); }
        .ql-route-box { border-top:1px solid var(--border-soft,#f1f5f9); margin-top:12px; padding-top:12px; }
        .ql-route-h { font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:var(--text-muted,#64748b); margin-bottom:8px; }
        .ql-rtargets { display:flex; flex-wrap:wrap; gap:6px; margin-bottom:10px; }
        .ql-rt { font-size:12.5px; padding:7px 12px; border:1px solid var(--border,#e2e8f0); border-radius:999px; background:var(--card,#fff); cursor:pointer; }
        .ql-rt.on { background:var(--accent,#2563eb); color:#fff; border-color:var(--accent,#2563eb); }
        .ql-btn { padding:10px 18px; border-radius:10px; font-size:14px; font-weight:600; cursor:pointer; border:1px solid transparent; }
        .ql-btn.primary { background:var(--accent,#2563eb); color:#fff; }
        .ql-btn.ghost { background:none; border-color:var(--border,#e2e8f0); color:var(--text-muted,#64748b); }
        @media (max-width:640px){ .ql-grid { grid-template-columns:repeat(auto-fill,minmax(150px,1fr)); } }
      </style>
      <div class="ql-hero">
        <div>
          <h2>Quick Log</h2>
          <p>Walk a property, snap what needs attention, and it's logged in seconds — one issue per photo if you like. Everything here also flows onto the Issues board.</p>
        </div>
        <button class="ql-log-btn" id="ql-open">＋ Log issues</button>
      </div>
      <div class="ql-kpis">
        <div class="ql-kpi"><div class="ql-kpi-v">${kpis.total}</div><div class="ql-kpi-l">Total logged</div></div>
        <div class="ql-kpi"><div class="ql-kpi-v">${kpis.open}</div><div class="ql-kpi-l">Open</div></div>
        <div class="ql-kpi"><div class="ql-kpi-v">${kpis.week}</div><div class="ql-kpi-l">This week</div></div>
        <div class="ql-kpi"><div class="ql-kpi-v">${kpis.photos}</div><div class="ql-kpi-l">With photos</div></div>
      </div>
      <div class="ql-filters">
        <input class="ql-sel" id="ql-prop" list="ql-prop-dl" autocomplete="off" placeholder="All properties — type to filter" value="${this.fListing === 'all' ? '' : this.esc((props.find(p => p.id === this.fListing) || {}).name || '')}" style="min-width:220px;">
        <datalist id="ql-prop-dl">${props.map(p => `<option value="${this.esc(p.name)}"></option>`).join('')}</datalist>
        <input type="date" class="ql-sel" id="ql-from" value="${this.fFrom}" title="Logged on or after">
        <input type="date" class="ql-sel" id="ql-to" value="${this.fTo}" title="Logged on or before">
        ${['open', 'in_progress', 'resolved', 'all'].map(s => `<button class="ql-chip ${this.fStatus === s ? 'on' : ''}" data-status="${s}">${s === 'all' ? 'All' : s.replace('_', ' ')}</button>`).join('')}
        <button class="ql-chip ${this.groupByProp ? 'on' : ''}" id="ql-group">▦ Group by property</button>
        <button class="ql-chip ${this.selectMode ? 'on' : ''}" id="ql-selmode">☑ Select</button>
        ${this.selectMode ? `
          <button class="ql-chip" id="ql-selall">Select all shown</button>
          <button class="ql-chip" id="ql-bulkdel" ${this.selected.size && !this.bulkBusy ? '' : 'disabled'} style="border-color:#fca5a5; color:#b91c1c;">${this.bulkBusy ? 'Deleting…' : `🗑 Delete ${this.selected.size} selected`}</button>
          <button class="ql-chip" id="ql-selcancel">Cancel</button>` : ''}
      </div>
      ${(() => {   // R42: paint at most `shown` cards; the rest are one tap away
        if (!rows.length) return emptyState('Nothing logged yet', 'Tap “Log issues”, pick a property, and start capturing. Photos, voice notes, the works.', '📸');
        const visible = this.visibleRows(rows);
        // Selection invariant: selected ⊆ visible — a shown-reset or refresh
        // must never leave invisible cards armed for bulk delete.
        if (this.selectMode && this.selected.size) {
          const vis = new Set(visible.map(r => String(r.source_id)));
          this.selected = new Set([...this.selected].filter(id => vis.has(id)));
        }
        const rest = rows.length - visible.length;
        // Grouped view gets the FULL rows (true per-property counts + true
        // largest-first order) with the card budget applied inside groups.
        const grid = this.groupByProp ? this.groupedHtml(rows, this.shown) : `<div class="ql-grid">${visible.map(r => this.cardHtml(r)).join('')}</div>`;
        return grid + (rest > 0 ? `<div style="text-align:center; margin:14px 0;"><button class="ql-chip" id="ql-more" style="padding:10px 22px; font-size:13.5px;">Show ${Math.min(160, rest)} more · ${rest} remaining</button></div>` : '');
      })()}
      ${this.detail ? this.detailHtml() : ''}`;
    this.bind();
  }

  // R42: the ACTUAL rendered set for the current view mode — grouped mode
  // distributes the budget across largest-first groups, so "visible" is NOT
  // the first `shown` of the flat list. Selection logic must use this.
  private visibleRows(rows: any[]): any[] {
    if (!this.groupByProp) return rows.slice(0, this.shown);
    const groups = new Map<string, any[]>();
    for (const r of rows) { const k = String(r.listing_id); if (!groups.has(k)) groups.set(k, []); groups.get(k)!.push(r); }
    const sorted = [...groups.entries()].sort((a, b) => b[1].length - a[1].length);
    let left = this.shown; const out: any[] = [];
    for (const [, list] of sorted) {
      const take = Math.min(list.length, left);
      out.push(...list.slice(0, take));
      left -= take;
      if (left <= 0) break;
    }
    return out;
  }

  // R42: groups + headers come from the FULL filtered rows (true counts, true
  // largest-first order); only the CARDS respect the shared render budget.
  private groupedHtml(rows: any[], budget = Infinity): string {
    const groups = new Map<string, any[]>();
    for (const r of rows) { const k = String(r.listing_id); if (!groups.has(k)) groups.set(k, []); groups.get(k)!.push(r); }
    const sorted = [...groups.entries()].sort((a, b) => b[1].length - a[1].length);
    let left = budget;
    return sorted.map(([, list]) => {
      const take = Math.max(0, Math.min(list.length, left));
      left -= take;
      const cards = list.slice(0, take);
      const hidden = list.length - take;
      return `
      <div style="margin-bottom:18px;">
        <div style="font-size:13px; font-weight:700; color:var(--text,#0f172a); margin-bottom:8px;">${this.esc(list[0].listing_name)} <span style="color:var(--text-muted,#94a3b8); font-weight:500;">· ${list.length}${hidden > 0 ? ` (showing ${take})` : ''}</span></div>
        ${cards.length ? `<div class="ql-grid">${cards.map(r => this.cardHtml(r)).join('')}</div>` : ''}
      </div>`;
    }).join('');
  }

  private cardHtml(r: any): string {
    const emoji = CAT_EMOJI[r.type] || '•';
    const sel = this.selectMode && this.selected.has(String(r.source_id));
    return `<div class="ql-card" data-open="${this.esc(r.source_id)}" style="${sel ? 'outline:3px solid var(--accent,#2563eb); outline-offset:-1px;' : ''}">
      ${this.selectMode ? `<span style="position:absolute; top:6px; left:6px; z-index:2; width:22px; height:22px; border-radius:6px; display:flex; align-items:center; justify-content:center; font-size:13px; font-weight:700; background:${sel ? 'var(--accent,#2563eb)' : 'rgba(255,255,255,.92)'}; color:${sel ? '#fff' : 'var(--text-muted,#64748b)'}; border:1px solid ${sel ? 'var(--accent,#2563eb)' : 'var(--border,#e2e8f0)'};">${sel ? '✓' : ''}</span>` : ''}
      <div class="ql-ph">
        ${r.thumb ? `<img src="${this.esc(r.thumb)}" alt="" loading="lazy" decoding="async">` : `<span class="ql-nph">${emoji}</span>`}
        <span class="ql-sev-tag" style="${SEV_STYLE[r.severity] || ''}">${this.esc(r.severity)}</span>
        ${r.photos ? `<span class="ql-pn">📷 ${r.photos}</span>` : ''}
      </div>
      <div class="ql-cbody">
        <div class="ql-ctitle">${emoji} ${this.esc(r.title)}</div>
        <div class="ql-cmeta">${this.esc(r.listing_name)} · ${this.fmtD(r.created_at)}</div>
        <span class="ql-cstatus st-${r.status}">${this.esc(String(r.status).replace('_', ' '))}</span>
        ${r.wo && r.wo.chip ? `<span class="ql-cstatus ql-wo" style="${woTone(r.wo.chip)}" title="${this.esc(r.wo.chip === 'missing' ? 'Its work order was deleted — route it again if it still needs doing' : `Work order${r.wo.title ? ` “${String(r.wo.title).slice(0, 60)}”` : ''}`)}">${this.esc(woChipLabel(r.wo))}</span>` : ''}
        ${r.proof === 'missing' ? '<span class="ql-cstatus" style="background:#fef3c7; color:#92400e;" title="Marked fixed without an after photo">⚠ no after photo</span>' : r.after_n ? '<span class="ql-cstatus" style="background:#dcfce7; color:#166534;">📷 after ✓</span>' : ''}
        ${r.supply_hint && r.supply_hint.item && !r.supply_hint.order_id ? `<span class="ql-cstatus" style="background:#fffbeb; color:#92400e;" title="The note says this supply is out">📦 ${this.esc(r.supply_hint.item)}?</span>` : ''}
        ${r.info && r.info.unread ? '<span class="ql-cstatus" style="background:#dbeafe; color:#1d4ed8;">💬 new info</span>' : ''}
      </div>
    </div>`;
  }

  private detailHtml(): string {
    const d = this.detail;
    const x = d.logged || {};
    // R116 #5: the record's own video list + the shared isVideoUrl (an extension-only
    // test drew /video/ clips without one as broken images); queued files show too.
    const media = [...(x.photos || []), ...(x.videos || [])];
    const id = String(d.source_id || x.id);
    const pendBefore = pendingItems(`/issues/log/${encodeURIComponent(id)}/photo`, (f) => f.kind !== 'after');
    const grid = media.length || pendBefore.length ? mediaStrip(toMediaItems(media, x.videos || []), { size: 'md', alt: 'issue photo', append: pendingTilesHtml(pendBefore) }) : '';
    const routed = x.routed || {};
    const boardRow = this.issues.find(i => String(i.source_id) === String(d.source_id || x.id)) || {};
    const woRef = boardRow.wo && boardRow.wo.chip !== 'missing' ? boardRow.wo : null;   // R116: the WO it is actually on (overlay or routed)
    // review R120 #14 (rounds 5 and 6): a ticket dismissed on the Issues board
    // is routed from here by the office only (the server refuses a field
    // login, even over its own dismissal), so a field login reads a note
    // instead of "Route it". The office keeps "Route it" and is never told to
    // ask the office. "Fixed it myself / Reopen" over someone else's board
    // dismissal is the office's too (the server refuses a field login); a
    // field login keeps it on a ticket it dismissed itself (dismissed_mine).
    const boardDismissed = boardRow.status === 'dismissed';
    const office = this.isOffice();
    const routeHidden = boardDismissed && !office;
    const fixedHidden = boardDismissed && !office && !boardRow.dismissed_mine;
    const dismissedNote = !boardDismissed ? ''
      : boardRow.dismissed_mine && !office
        ? `You dismissed this on the Issues board${boardRow.dismissed_reason ? `: ${this.esc(boardRow.dismissed_reason)}` : ''}. Ask the office to restore it if it still needs doing.`
        : `Dismissed on the Issues board${boardRow.dismissed_by ? ` by ${this.esc(boardRow.dismissed_by)}` : ''}${boardRow.dismissed_reason ? `: ${this.esc(boardRow.dismissed_reason)}` : ''}. ${office ? 'Restore it there, or route it below if it still needs doing.' : 'Ask the office to restore it if it still needs doing.'}`;
    const rbits = [routed.wo_id && !woRef ? 'work order' : '', routed.clean_turn_id ? 'next clean' : '', routed.focus_key ? 'inspection' : '', routed.supply_order_id ? 'supply request' : '', routed.task_id ? 'task' : '', routed.strike_candidate ? 'strike review' : ''].filter(Boolean);
    return `
      <div class="ql-modal-wrap">
        <div class="ql-modal-bg" data-close></div>
        <div class="ql-modal">
          <h3>${this.esc(x.title || d.title)}</h3>
          <div class="ql-modal-sub">${this.esc(x.category || '')} · ${this.esc(x.severity || '')}${x.room ? ` · 📍 ${this.esc(x.room)}` : ''}${x.created_by ? ` · by ${this.esc(x.created_by)}` : ''} · ${this.esc(d.listing_name || '')}</div>
          ${x.fixed ? `<div class="ql-modal-sub" style="color:#166534;"><b>✔ Resolved by ${this.esc(x.fixed_by || 'inspector')}</b> — documented for the record. Route it to Strike review below if it should count against whoever caused it.</div>` : ''}
          <div style="margin-bottom:10px;">
            <textarea id="ql-note-edit" maxlength="2000" placeholder="Notes — how bad, exactly where, by when…" style="width:100%; min-height:60px; font:inherit; font-size:13.5px; padding:8px 10px; border:1px solid var(--border,#e2e8f0); border-radius:10px; resize:vertical;">${this.esc(x.note && x.note !== x.title ? x.note : '')}</textarea>
            <button class="ql-btn ghost" id="ql-note-save" data-note-id="${this.esc(d.source_id || x.id)}" style="margin-top:5px; padding:5px 14px; font-size:12px;">Save notes</button>
          </div>
          ${(() => {   // R109 #a: before | after, so "fixed" can be checked at a glance
            const after: string[] = Array.isArray(x.after_photos) ? x.after_photos : [];
            // warn only where the board does (its proof rule: in-place fixes since after photos existed)
            const row = this.issues.find(i => String(i.source_id) === String(d.source_id || x.id)) || {};
            const flagged = row.proof === 'missing' || row.proof === 'skipped';
            const skipWhy = (x.fixed_skip && x.fixed_skip.reason) || row.fixed_skip_reason || row.after_skip_reason || '';
            // R116 #5: an after clip renders as a clip (it drew as a broken <img>); queued after files show uploading
            const pendAfter = pendingItems(`/issues/log/${encodeURIComponent(id)}/photo`, (f) => f.kind === 'after');
            const showAfter = after.length || flagged || pendAfter.length;
            const meta = new Map<string, any>((Array.isArray(x.after_meta) ? x.after_meta : []).filter((m: any) => m && m.url).map((m: any) => [String(m.url), m]));
            const afterItems = toMediaItems(after, [...(x.videos || []), ...[...meta.values()].filter((m: any) => m.media === 'video').map((m: any) => String(m.url))], (u) => { const m = meta.get(u); return ['After', m && m.by ? String(m.by).split('@')[0] : ''].filter(Boolean).join(' · '); });
            const afterGrid = after.length || pendAfter.length ? mediaStrip(afterItems, { size: 'md', cls: 'mx-after', alt: 'after photo', append: pendingTilesHtml(pendAfter) }) : '';
            const before = grid ? `<div style="margin:8px 0;">${grid}</div>` : '<div style="font-size:13px; color:var(--text-muted); margin:8px 0;">No photos on this one.</div>';
            if (!showAfter) return `<div data-ql-media>${before}</div>`;
            return `<div data-ql-media><div class="ql-route-h" style="margin-top:4px;">Before</div>${before}
              <div class="ql-route-h" style="margin-top:10px;">After</div>
              ${afterGrid ? `<div style="margin:8px 0;">${afterGrid}</div>`
                : skipWhy ? `<div class="ql-modal-sub">No after photo — ${this.esc(skipWhy)}${x.fixed_skip && x.fixed_skip.by ? ` (${this.esc(x.fixed_skip.by)})` : ''}</div>`
                : '<div class="ql-modal-sub" style="color:#92400e;">⚠ Marked fixed with no after photo — add one so the fix can be checked.</div>'}</div>`;
          })()}
          ${x.supply_hint && x.supply_hint.item && !x.supply_hint.dismissed ? (x.supply_hint.order_id
            ? `<div class="ql-modal-sub" style="color:#166534;">✓ 📦 ${this.esc(x.supply_hint.item)} requested</div>`
            : `<div class="ql-modal-sub" style="background:#fffbeb; border:1px solid #fcd34d; border-radius:10px; padding:8px 10px; display:flex; gap:8px; align-items:center; flex-wrap:wrap;">📦 The note says <b>${this.esc(x.supply_hint.item)}</b> is out. <button class="ql-btn primary" data-modal-supreq="${this.esc(d.source_id || x.id)}" style="padding:5px 12px; font-size:12.5px; background:#b45309;">Request it</button><button class="ql-btn ghost" data-modal-supno="${this.esc(d.source_id || x.id)}" style="padding:5px 12px; font-size:12.5px;">No thanks</button></div>`) : ''}
          ${woRef ? `<div class="ql-modal-sub" style="display:flex; align-items:center; gap:8px; flex-wrap:wrap;">On work order <b>${this.esc(String(woRef.title || '').slice(0, 70) || 'in Maintenance')}</b> <span class="ql-cstatus" style="margin-top:0; ${woTone(woRef.chip)}">${this.esc(woChipLabel(woRef))}</span> <button class="ql-btn ghost" data-ql-wo="${this.esc(woRef.id)}" style="padding:5px 12px; font-size:12px; min-height:36px;">Open in Maintenance ↗</button></div>` : boardRow.wo && boardRow.wo.chip === 'missing' ? '<div class="ql-modal-sub">Its work order was deleted in Maintenance — route it again below if it still needs doing.</div>' : ''}
          ${rbits.length ? `<div class="ql-modal-sub">Already routed to: <b>${this.esc(rbits.join(' · '))}</b></div>` : ''}
          <div class="ql-route-box">
            ${boardDismissed ? `<div class="ql-route-h">Dismissed</div>
            <div class="ql-modal-sub" data-ql-dismissed>${dismissedNote}</div>` : ''}
            ${routeHidden ? '' : `<div class="ql-route-h">Route it</div>
            <div class="ql-rtargets">${ROUTE_TARGETS.map(t => `<button class="ql-rt${this.routeSel.has(t.v) ? ' on' : ''}" data-rt="${t.v}">${t.label}</button>`).join('')}</div>
            <select class="ql-sel" id="ql-assignee" style="width:100%; margin-bottom:10px;"><option value="">Assign to… (optional — anyone on the team)</option>${this.roster.map(p => `<option value="${this.esc(p.name)}">${this.esc(p.name)} · ${this.esc(p.kind)}</option>`).join('')}</select>`}
            <div id="ql-route-msg" style="font-size:12.5px; color:var(--text-muted); margin-bottom:8px;"></div>
            <div style="display:flex; justify-content:space-between; gap:8px; flex-wrap:wrap;">
              <div style="display:flex; gap:8px; flex-wrap:wrap;">
                <button class="ql-btn ghost" data-del="${this.esc(d.source_id || x.id)}">Delete</button>
                <label class="ql-btn ghost" style="cursor:pointer;" title="Photos or a video — pick several at once">📷 Add photos / video<input type="file" accept="${EVIDENCE_ACCEPT}" multiple data-modal-photo="${this.esc(d.source_id || x.id)}" style="display:none;"></label>
                ${x.fixed ? `<label class="ql-btn ghost" style="cursor:pointer; color:#166534;" title="Photos or a video of the fix — several at once">📷 Add after photos / video<input type="file" accept="${EVIDENCE_ACCEPT}" multiple data-modal-after="${this.esc(d.source_id || x.id)}" style="display:none;"></label>` : ''}
                ${fixedHidden ? '' : `<button class="ql-btn ghost" data-modal-fixed="${this.esc(d.source_id || x.id)}" title="Resolved on the spot — stays documented; route to Strike review to count it against whoever caused it" style="${x.fixed ? '' : 'color:#166534;'}">${x.fixed ? '↩ Reopen' : '✔ Fixed it myself'}</button>`}
              </div>
              <div style="display:flex; gap:8px;">
                <button class="ql-btn ghost" data-close>Close</button>
                ${routeHidden ? '' : '<button class="ql-btn primary" id="ql-route-go">Route →</button>'}
              </div>
            </div>
          </div>
        </div>
      </div>`;
  }

  private routeSel = new Set<string>();
  private bind(): void {
    this.container.querySelector('#ql-open')?.addEventListener('click', () => {
      QuickLogIssue.open({
        sourceContext: 'standalone',
        onLogged: (n) => {
          void this.render();
          // Photo uploads outlive the modal (background pool) — re-pull the
          // list a couple of times so thumbs appear as uploads land.
          if (n) [4000, 12000].forEach(ms => setTimeout(() => void this.quietRefresh(), ms));
        },
      });
    });
    this.container.querySelector<HTMLInputElement>('#ql-prop')?.addEventListener('change', (e) => {
      const typed = (e.target as HTMLInputElement).value.trim().toLowerCase();
      this.shown = 80;   // R42: any filter change resets the render window
      if (!typed) { this.fListing = 'all'; this.draw(); return; }
      const hit = [...new Map(this.issues.map(i => [String(i.listing_id), i.listing_name])).entries()].find(([, name]) => String(name).toLowerCase() === typed);
      this.fListing = hit ? hit[0] : 'all';
      this.draw();
    });
    this.container.querySelector<HTMLInputElement>('#ql-from')?.addEventListener('change', (e) => { this.fFrom = (e.target as HTMLInputElement).value; this.shown = 80; this.draw(); });
    this.container.querySelector<HTMLInputElement>('#ql-to')?.addEventListener('change', (e) => { this.fTo = (e.target as HTMLInputElement).value; this.shown = 80; this.draw(); });
    this.container.querySelectorAll<HTMLElement>('[data-status]').forEach(el => el.addEventListener('click', () => { this.fStatus = el.dataset.status!; this.shown = 80; this.draw(); }));
    this.container.querySelector('#ql-group')?.addEventListener('click', () => { this.groupByProp = !this.groupByProp; this.shown = 80; this.draw(); });
    this.container.querySelector('#ql-more')?.addEventListener('click', () => { this.shown += 160; this.draw(); });
    // R35 #5: multi-select mode — cards toggle selection instead of opening.
    this.container.querySelector('#ql-selmode')?.addEventListener('click', () => { this.selectMode = !this.selectMode; this.selected.clear(); this.draw(); });
    this.container.querySelector('#ql-selcancel')?.addEventListener('click', () => { this.selectMode = false; this.selected.clear(); this.draw(); });
    this.container.querySelector('#ql-selall')?.addEventListener('click', () => {
      for (const r of this.visibleRows(this.filtered())) this.selected.add(String(r.source_id));   // R42: "shown" means VISIBLE (view-mode aware)
      this.draw();
    });
    this.container.querySelector('#ql-bulkdel')?.addEventListener('click', async () => {
      if (this.bulkBusy || !this.selected.size) return;
      if (!confirm(`Delete ${this.selected.size} logged issue${this.selected.size === 1 ? '' : 's'}? This can't be undone.`)) return;
      await this.bulkDelete([...this.selected]);
    });
    this.container.querySelectorAll<HTMLElement>('[data-open]').forEach(el => el.addEventListener('click', () => {
      if (this.selectMode) {
        const id = String(el.dataset.open);
        if (this.selected.has(id)) this.selected.delete(id); else this.selected.add(id);
        this.draw();
        return;
      }
      void this.openDetail(el.dataset.open!);
    }));
    // detail modal
    this.container.querySelectorAll<HTMLElement>('[data-close]').forEach(el => el.addEventListener('click', () => { this.detail = null; this.routeSel.clear(); this.draw(); }));
    this.container.querySelectorAll<HTMLElement>('[data-rt]').forEach(el => el.addEventListener('click', () => {
      const v = el.dataset.rt!;
      if (this.routeSel.has(v)) this.routeSel.delete(v); else this.routeSel.add(v);
      el.classList.toggle('on');
    }));
    this.container.querySelector('#ql-route-go')?.addEventListener('click', () => void this.doRoute());
    this.container.querySelector<HTMLElement>('[data-del]')?.addEventListener('click', (e) => void this.doDelete((e.currentTarget as HTMLElement).dataset.del!));
    this.container.querySelector<HTMLElement>('[data-ql-wo]')?.addEventListener('click', (e) => {   // R116: open the WO it is on
      try { sessionStorage.setItem('mn_focus_wo', (e.currentTarget as HTMLElement).dataset.qlWo || ''); } catch { /* private mode */ }
      this.detail = null; document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'maintenance' }));
    });
    // R28: "resolved by inspector" — toggle fixed from the detail modal; the
    // issue stays documented (and strike-routable), it just stops being open work.
    // R44 (Mark): editable notes on the issue — the note field always existed
    // in the data; this is its first edit surface on the board.
    this.container.querySelector<HTMLButtonElement>('#ql-note-save')?.addEventListener('click', async (e) => {
      const btn = e.currentTarget as HTMLButtonElement;
      const id = btn.dataset.noteId!;
      const ta = this.container.querySelector<HTMLTextAreaElement>('#ql-note-edit');
      if (!ta) return;
      btn.disabled = true; btn.textContent = 'Saving…';
      try {
        const r = await fetch(`/api/assetops/issues/log/${encodeURIComponent(id)}`, {
          method: 'POST', headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ note: ta.value }),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        if (this.detail && this.detail.logged) this.detail.logged.note = ta.value;
        btn.textContent = '✓ Saved'; setTimeout(() => { btn.disabled = false; btn.textContent = 'Save notes'; }, 1500);
      } catch (err: any) {
        alert(`Save failed — ${err.message}`);
        btn.disabled = false; btn.textContent = 'Save notes';
      }
    });
    this.container.querySelector<HTMLElement>('[data-modal-fixed]')?.addEventListener('click', async (e) => {
      const id = (e.currentTarget as HTMLElement).dataset.modalFixed!;
      const x = (this.detail && this.detail.logged) || {};
      const toFixed = !x.fixed;
      // R109 #a: marking it fixed asks for the AFTER photo (or why there isn't one)
      let proof: Awaited<ReturnType<typeof askAfterPhoto>> = null;
      if (toFixed) { proof = await askAfterPhoto({ title: x.title, verb: 'Mark fixed' }); if (!proof) return; }
      const body = JSON.stringify({ fixed: toFixed, fixed_by: toFixed ? this.who() : null, ...(proof && proof.skip_reason ? { fixed_skip_reason: proof.skip_reason } : {}) });
      // review R120 #14 (round 6): the Issues board unreadable (503
      // board_unknown, nothing saved) → plain words + Try again, which re-sends
      // the same update (the after photo is already queued: never asked twice)
      const send = async (): Promise<void> => {
        try {
          const r = await fetch(`${API}/issues/log/${encodeURIComponent(id)}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body });
          const j = await r.json().catch(() => null);
          if (isBoardUnknown(r.status, j)) { offerBoardRetry(send); return; }
          if (!r.ok) throw new Error((j && j.error) || `HTTP ${r.status}`);
          if (j && j.issue && this.detail && this.detail.logged && String(this.detail.logged.id || this.detail.source_id) === String(id)) this.detail.logged = { ...this.detail.logged, ...j.issue };
        } catch (err: any) { alert(`Update failed — ${err.message}`); }
        finally { await this.load(); this.draw(); }   // the button always reflects the server
      };
      try {
        // the photo first — a failed upload leaves the issue as it was, so a retry is a retry
        if (proof && proof.files && proof.files.length) await this.uploadAfter(id, proof.files);
      } catch (err: any) { alert(`Update failed — ${err.message}`); await this.load(); this.draw(); return; }
      await send();
    });
    this.container.querySelector<HTMLInputElement>('[data-modal-after]')?.addEventListener('change', async (e) => {
      const inp = e.target as HTMLInputElement;
      const id = inp.dataset.modalAfter!;
      const files = Array.from(inp.files || []); inp.value = '';
      if (!files.length || inp.disabled) return;
      inp.disabled = true;   // double-pick guard
      try { await this.uploadAfter(id, files); this.patchMedia(); }
      catch (err: any) { alert(`Photo upload failed — ${err.message}`); }
      finally { inp.disabled = false; }
    });
    // R109 #e: the supply the note names ("…no windex") → one-tap request
    this.container.querySelector<HTMLButtonElement>('[data-modal-supreq]')?.addEventListener('click', (e) => {
      const btn = e.currentTarget as HTMLButtonElement;
      void this.requestSupply(btn.dataset.modalSupreq!, btn);
    });
    this.container.querySelector<HTMLButtonElement>('[data-modal-supno]')?.addEventListener('click', async (e) => {
      const id = (e.currentTarget as HTMLButtonElement).dataset.modalSupno!;
      await fetch(`${API}/issues/log/${encodeURIComponent(id)}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ supply_hint_dismissed: true }) }).catch(() => null);
      if (this.detail && this.detail.logged && this.detail.logged.supply_hint) this.detail.logged.supply_hint.dismissed = true;
      await this.load(); this.draw();
    });
    // R27 (Mark): "go back to add photos" — add photos to any logged issue from
    // its detail modal; the modal grid re-renders from the server's response.
    this.container.querySelector<HTMLInputElement>('[data-modal-photo]')?.addEventListener('change', async (e) => {
      const inp = e.target as HTMLInputElement;
      const id = inp.dataset.modalPhoto!;
      const files = Array.from(inp.files || []);
      inp.value = '';
      if (!files.length || inp.disabled) return;
      // R116 #5: every picked photo / clip → the persisted queue (one per request,
      // who added it); the modal shows them uploading and refreshes as they land
      inp.disabled = true;   // double-pick guard
      try {
        const n = await enqueuePhotos(`${API}/issues/log/${encodeURIComponent(id)}/photo`, files, { by: this.who() }, 'issue photo');
        if (!n) alert('Those files could not be queued — too large? Try a shorter clip.');
        else this.toast(`${pickSummary(files)} queued — uploading in the background.`);
        this.patchMedia();
      } catch (err: any) { alert(`Couldn't queue the files — ${err.message}`); }
      finally { inp.disabled = false; }
    });
    bindMediaStrips(this.container);
  }

  private who(): string {
    try { const p = JSON.parse(sessionStorage.getItem('userProfile') || '{}'); return String(p.full_name || p.email || sessionStorage.getItem('userRole') || '').trim(); } catch (_) { return ''; }
  }
  // review R120 #14 (round 6): the office roles (the server's R120_OFFICE_ROLES)
  private isOffice(): boolean {
    return ['admin', 'director', 'manager', 'remote_ops'].includes(String(sessionStorage.getItem('userRole') || ''));
  }
  // R35 #5: delete the selected tickets. The server caps a batch at 500, so
  // chunk ("Select all" on a big board deletes everything) and report
  // honestly if any chunk fails. Review R120 #14 (round 6): the Issues board
  // unreadable refuses a chunk (503 board_unknown, nothing deleted); those
  // ids are offered again with Try again (the user already confirmed).
  private async bulkDelete(ids: string[]): Promise<void> {
    if (this.bulkBusy || !ids.length) return;
    this.bulkBusy = true; this.draw();
    let removed = 0, failed = false, kept = 0;
    const unknown: string[] = [];
    for (let i = 0; i < ids.length; i += 400) {
      const chunk = ids.slice(i, i + 400);
      const resp = await fetch(`${API}/issues/log/bulk-delete`, {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ ids: chunk }),
      }).catch(() => null);
      const r: any = resp ? await resp.json().catch(() => null) : null;
      if (resp && isBoardUnknown(resp.status, r)) { unknown.push(...chunk); continue; }
      // review R120 #14 (round 5): tickets someone else dismissed on the
      // Issues board stay (the office's to delete) and come back as skipped
      if (resp && resp.status === 403 && r && Array.isArray(r.skipped)) { kept += r.skipped.length; continue; }
      if (!resp || !resp.ok || !r) { failed = true; break; }
      removed += Number(r.removed) || 0;
      const sk = Array.isArray(r.skipped) ? r.skipped : [];
      unknown.push(...sk.filter((x: any) => x && x.code === 'board_unknown').map((x: any) => String(x.board_id || '').replace(/^log:/, '')).filter(Boolean));
      kept += sk.filter((x: any) => !(x && x.code === 'board_unknown')).length;
    }
    this.bulkBusy = false;
    if (failed) { alert(`Delete stopped partway — ${removed} of ${ids.length} removed. The rest are still selected; try again.`); await this.load(); this.draw(); return; }
    if (kept) alert(`${removed} removed. ${kept} stayed: ${kept === 1 ? 'it was' : 'they were'} dismissed on the Issues board by someone else, so only the office can delete ${kept === 1 ? 'it' : 'them'}.`);
    this.selected.clear(); this.selectMode = false;
    await this.load();
    this.draw();
    if (unknown.length) offerBoardRetry(() => this.bulkDelete(unknown));
  }
  // R109 #a: after photos (compressed, one per request) — stored apart from
  // the "before" photos; the detail re-reads them from the server's answer.
  // R116 #5: photos AND clips through the persisted background queue, with who
  // took them and the time of the fix they prove (for_resolved_at — a file still
  // uploading when the fix is reopened is kept for the record, never as proof for
  // the next one). Returns once QUEUED: marking it fixed never waits for an upload.
  private async uploadAfter(id: string, files: File[]): Promise<void> {
    const n = await enqueuePhotos(`${API}/issues/log/${encodeURIComponent(id)}/photo`, files, { kind: 'after', by: this.who(), for_resolved_at: new Date().toISOString() }, 'after photo');
    if (!n) throw new Error('none of those files could be queued (too large?) — pick a shorter clip or a photo');
  }
  /** R116 #5: redraw only the open detail's before / after media (a full draw would drop a half-typed note). */
  private patchMedia(): void {
    const host = this.container.querySelector<HTMLElement>('[data-ql-media]');
    if (!host || !this.detail) return;
    const tmp = document.createElement('div');
    tmp.innerHTML = this.detailHtml();
    const fresh = tmp.querySelector<HTMLElement>('[data-ql-media]');
    if (fresh) { host.innerHTML = fresh.innerHTML; bindMediaStrips(host); }
  }
  /** One listener for the page's lifetime: the open issue's file lands → re-read it and patch its media. */
  private listenEvidence(): void {
    const openId = () => (this.detail ? String(this.detail.source_id || (this.detail.logged && this.detail.logged.id) || '') : '');
    const isOpen = (url: string) => { const id = openId(); return !!id && String(url || '').endsWith(`/issues/log/${encodeURIComponent(id)}/photo`); };
    let timer = 0;
    window.addEventListener('surge-photo-uploaded', (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      // the app's pages share one content container — act only while THIS page's detail is on screen
      if (!this.container.isConnected || !isOpen(d.url) || !this.container.querySelector('[data-ql-media]')) return;
      const iss = d.response && d.response.issue;
      if (iss && this.detail && this.detail.logged && String(iss.id) === openId()) this.detail.logged = mergeLandedMedia({ ...this.detail.logged }, iss);
      this.patchMedia();
      window.clearTimeout(timer);
      timer = window.setTimeout(() => { void this.load(); }, 1200);   // the gallery's counts / proof chips behind the modal (the modal is untouched)
    });
    window.addEventListener('surge-photo-queue', () => { if (this.container.isConnected && this.detail) this.patchMedia(); });
    window.addEventListener('surge-photo-progress', (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      if (this.container.isConnected && isOpen(d.url)) patchPendingProgress(this.container, String(d.clientKey || ''), Number(d.sent) || 0, Number(d.total) || 0);
    });
  }

  private async openDetail(id: string): Promise<void> {
    const listRow = this.issues.find(i => String(i.source_id) === String(id)) || {};
    const d = await fetch(`${API}/issues/detail`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ board_id: `log:${id}` }) }).then(r => r.ok ? r.json() : null).catch(() => null);
    this.detail = d ? { ...d, source_id: id, listing_name: listRow.listing_name, title: listRow.title } : { source_id: id, logged: { id, title: listRow.title, photos: [], videos: [] }, listing_name: listRow.listing_name };
    this.routeSel.clear();
    this.draw();
  }

  private async doRoute(): Promise<void> {
    const id = this.detail?.source_id;
    if (!id) return;
    // The DOM is the single source of truth — read the highlighted chips directly
    // so an app auto-refresh that re-renders the modal can never desync selection.
    const targets = [...this.container.querySelectorAll<HTMLElement>('.ql-rt.on')].map(el => el.dataset.rt!).filter(Boolean);
    const msg = this.container.querySelector('#ql-route-msg');
    if (!targets.length) { if (msg) msg.textContent = 'Pick where it should go.'; return; }
    const assigned_to = this.container.querySelector<HTMLSelectElement>('#ql-assignee')?.value || '';
    await this.sendRoute(String(id), targets, assigned_to);
  }
  // One route request (doRoute; Try again re-sends the same one: review R120
  // #14 round 6). The modal's message line and button, when still on screen.
  private async sendRoute(id: string, targets: string[], assigned_to: string): Promise<void> {
    const msg = this.container.querySelector('#ql-route-msg');
    const btn = this.container.querySelector<HTMLButtonElement>('#ql-route-go');
    if (btn) btn.disabled = true;
    if (msg) msg.textContent = 'Routing…';
    try {
      const ctrl = new AbortController();
      const to = setTimeout(() => ctrl.abort(), 15000);
      const r = await fetch(`${API}/issues/log/${id}/route`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ targets, assigned_to: assigned_to || undefined }), signal: ctrl.signal });
      clearTimeout(to);
      const j = await r.json().catch(() => ({} as any));
      // the Issues board unreadable: nothing was routed; plain words + Try again
      if (isBoardUnknown(r.status, j)) { if (msg) msg.textContent = BOARD_UNKNOWN_MSG; if (btn) btn.disabled = false; offerBoardRetry(() => this.sendRoute(id, targets, assigned_to)); return; }
      if (!r.ok) { if (msg) msg.textContent = j.error || 'Route failed — try again.'; if (btn) btn.disabled = false; return; }
      // The server returns 200 even when a target soft-fails (e.g. no upcoming clean).
      let results = j.results || {};
      // R104 #6 (review #8): a supply request refused as a staging miss — confirm, then re-send with force_order
      if (results.supply && results.supply.code === 'cleaning_miss') {
        if (confirm(`${results.supply.error}\n\nOrder anyway (closet confirmed empty)?`)) {
          const r2 = await fetch(`${API}/issues/log/${id}/route`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ targets: ['supply'], assigned_to: assigned_to || undefined, force_order: true }) }).catch(() => null);
          const j2 = r2 ? await r2.json().catch(() => ({} as any)) : null;
          results = { ...results, supply: (r2 && r2.ok && j2 && j2.results && j2.results.supply) ? j2.results.supply : { error: (j2 && j2.error) || 'supply request failed — try again' } };
        } else {
          results = { ...results, supply: { error: 'supply request skipped — it was logged as a cleaning miss' } };
        }
      }
      const errs = Object.entries(results).filter(([, v]: any) => v && v.error).map(([k, v]: any) => `${k.replace('_', ' ')}: ${v.error}`);
      const ok = Object.entries(results).some(([, v]: any) => v && !v.error);
      if (errs.length && !ok) { if (msg) msg.textContent = errs.join(' · '); if (btn) btn.disabled = false; return; }
      // At least one target routed — close + refresh (note any partial failures).
      this.detail = null; this.routeSel.clear();
      await this.render();
      if (errs.length) this.toast(`Routed, but: ${errs.join(' · ')}`);
    } catch (e: any) {
      if (msg) msg.textContent = e?.name === 'AbortError' ? 'Timed out — try again.' : 'Network error — try again.';
      if (btn) btn.disabled = false;
    }
  }

  // R109 #e: the supply the note names ("…no windex") → one-tap request.
  // Review R120 #14 (round 6): the Issues board unreadable (503 board_unknown,
  // nothing ordered) → plain words + Try again (the same request).
  private async requestSupply(id: string, btn: HTMLButtonElement | null, force = false): Promise<void> {
    const reset = () => { if (btn && btn.isConnected) { btn.disabled = false; btn.textContent = 'Request it'; } };
    if (btn) { btn.disabled = true; btn.textContent = 'Requesting…'; }
    const send = (f: boolean) => fetch(`${API}/issues/log/${encodeURIComponent(id)}/supply-need`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ by: this.who() || undefined, ...(f ? { force_order: true } : {}) }) });
    try {
      let r = await send(force); let j = await r.json().catch(() => ({} as any));
      if (!force && r.status === 409 && j.code === 'cleaning_miss') {
        if (!confirm(j.error)) { reset(); return; }
        force = true;
        r = await send(true); j = await r.json().catch(() => ({} as any));
      }
      if (isBoardUnknown(r.status, j)) { reset(); const f = force; offerBoardRetry(() => this.requestSupply(id, null, f)); return; }
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      const open = this.detail && this.detail.logged && String(this.detail.source_id || this.detail.logged.id) === String(id);
      const it = j.item || (open && this.detail.logged.supply_hint && this.detail.logged.supply_hint.item) || 'Supply';
      if (open) this.detail.logged.supply_hint = { ...(this.detail.logged.supply_hint || {}), item: it, order_id: j.order_id };
      this.toast(j.already ? `Already requested (${it}).` : `📦 ${it} requested.`);
      await this.load(); this.draw();
    } catch (err: any) { reset(); alert(`Couldn't request it — ${err.message}`); }
  }

  private toast(text: string): void {
    const t = document.createElement('div');
    t.style.cssText = 'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);z-index:9500;background:#0f172a;color:#fff;padding:12px 18px;border-radius:12px;font-size:14px;box-shadow:0 8px 30px rgba(0,0,0,.3);max-width:90vw;text-align:center;';
    t.textContent = text; document.body.appendChild(t);
    setTimeout(() => t.remove(), 4000);
  }

  private async doDelete(id: string): Promise<void> {
    if (!id) return;
    this.detail = null; this.routeSel.clear();
    const r = await fetch(`${API}/issues/log/${id}`, { method: 'DELETE' }).catch(() => null);
    // review R120 #14 (round 5): a ticket someone else dismissed on the Issues
    // board is the office's to delete; say why instead of silently keeping it.
    // Round 6: the board unreadable → plain words + Try again (nothing deleted)
    let unknown = false;
    if (r && !r.ok) { const j = await r.json().catch(() => null); if (isBoardUnknown(r.status, j)) unknown = true; else if (j && j.error) alert(String(j.error)); }
    await this.render();
    if (unknown) offerBoardRetry(() => this.doDelete(id));
  }

  private fmtD(iso: string): string {
    if (!iso) return '';
    const d = new Date(iso); if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
}
